import React from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";

// Public-facing Terms of Service. Written for candidates + recruiters both.
// KEEP IN SYNC with /app/backend/services/consents.py::TERMS_VERSION when
// substantive text changes so past consent records are still verifiable.
export const TERMS_LAST_UPDATED = "15 February 2026";

export default function TermsOfService() {
    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="terms-page">
            <header className="bg-ink text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Brand variant="light" size="h-7" to={null} />
                    </Link>
                    <Link
                        to="/"
                        data-testid="terms-back-home"
                        className="text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white inline-flex items-center gap-1"
                    >
                        <ArrowLeft size={12} weight="bold" /> Home
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <div className="inline-flex items-center gap-2 mb-5 bg-ink text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
                    <ShieldCheck size={12} weight="bold" className="text-cobalt" />
                    Legal · Terms of Service
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
                    Terms of <span className="text-cobalt">Service.</span>
                </h1>
                <p className="text-xs text-[#A3A3A3] mb-10">
                    Version <span className="font-mono">v1.0-2026-02</span> · Last updated {TERMS_LAST_UPDATED}
                </p>

                <div className="prose prose-sm max-w-none text-[#262626] leading-relaxed space-y-6">
                    <Section title="1. Who these terms apply to">
                        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of NaukriDaddy
                        (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;NaukriDaddy&rdquo;), whether you use it as a
                        job seeker (&ldquo;Candidate&rdquo;) or a hiring team (&ldquo;Recruiter&rdquo;).
                        By creating an account, you accept these Terms in full.
                    </Section>

                    <Section title="2. Your account">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>You must be at least 18 years old to create an account.</li>
                            <li>You&apos;re responsible for keeping your credentials safe. Any activity from your account is treated as yours.</li>
                            <li>Recruiter accounts are invite-only and issued by an admin.</li>
                        </ul>
                    </Section>

                    <Section title="3. Acceptable use">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>No scraping, crawling, or automated data extraction without our written permission.</li>
                            <li>No impersonation, fake credentials, or fraudulent applications.</li>
                            <li>No spam, harassment, or unlawful outreach to candidates or recruiters.</li>
                            <li>No reverse-engineering our AI matching or resume-parsing models.</li>
                        </ul>
                    </Section>

                    <Section title="4. Content you upload">
                        <p>
                            You retain ownership of your resume, cover letters, and profile
                            content. By uploading, you grant us a worldwide, royalty-free
                            licence to store, index, translate, extract features from, and
                            display that content to (a) matching recruiters, and (b) our
                            AI scoring engine, strictly for the purpose of running the
                            NaukriDaddy platform.
                        </p>
                    </Section>

                    <Section title="5. AI-generated match scores">
                        <p>
                            Match scores, screening question suggestions, and AI-generated
                            summaries are <em>advisory only</em> and are not employment
                            decisions. Recruiters remain solely responsible for hiring
                            outcomes.
                        </p>
                    </Section>

                    <Section title="6. Payments & refunds">
                        <p>
                            Paid plans (Workspace Subscriptions, Candidate Boost) are billed
                            via Razorpay in Indian Rupees. All payments are non-refundable
                            once the plan is activated, except where required by law.
                        </p>
                    </Section>

                    <Section title="7. Termination">
                        <p>
                            You may delete your account at any time. We may suspend or
                            terminate accounts that violate these Terms, with or without
                            notice. On termination, we retain data only as long as required
                            by law or to defend claims.
                        </p>
                    </Section>

                    <Section title="8. Disclaimers & liability">
                        <p>
                            NaukriDaddy is provided &ldquo;as is&rdquo;. To the maximum extent permitted
                            by law, we exclude all warranties and our aggregate liability
                            to you for any claim is capped at the fees you paid us in the
                            12 months preceding the claim.
                        </p>
                    </Section>

                    <Section title="9. Governing law">
                        <p>
                            These Terms are governed by the laws of India. Disputes are
                            subject to the exclusive jurisdiction of the courts of Bengaluru,
                            Karnataka.
                        </p>
                    </Section>

                    <Section title="10. Contact">
                        <p>
                            Questions about these Terms? Email{" "}
                            <a href="mailto:legal@naukridaddy.com" className="text-cobalt font-bold underline">
                                legal@naukridaddy.com
                            </a>.
                        </p>
                    </Section>
                </div>

                <div className="mt-14 pt-6 border-t border-[#E5E5E5] flex flex-wrap items-center gap-4 text-xs text-[#525252]">
                    <Link to="/legal/privacy" data-testid="terms-goto-privacy" className="font-bold hover:text-cobalt underline underline-offset-2">
                        Read the Privacy Policy →
                    </Link>
                    <Link to="/candidate/signup" data-testid="terms-goto-signup" className="font-bold hover:text-cobalt underline underline-offset-2">
                        Back to signup →
                    </Link>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-heading text-lg font-black tracking-tight mb-2 text-ink">{title}</h2>
            <div className="text-[#404040]">{children}</div>
        </section>
    );
}
